import React, { useRef } from 'react';
import AnnotationPin from './AnnotationPin';
import { FigmaAnnotationResponse } from '../types/annotation';

interface FigmaCanvasProps {
  frameId?: string | null;
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  annotations: FigmaAnnotationResponse[];
  selectedAnnotationId: number | null;
  highlightRect?: { left: number; top: number; width: number; height: number } | null;
  imageLoading?: boolean;
  getAnnotationPinPosition?: (annotation: FigmaAnnotationResponse) => { x: number; y: number } | null;
  canvasState: { scale: number; offsetX: number; offsetY: number };
  mode: 'view' | 'annotate';
  onCanvasClick: (xPercent: number, yPercent: number) => void;
  onCanvasHover?: (x: number, y: number) => void;
  onCanvasLeave?: () => void;
  onAnnotationClick: (annotation: FigmaAnnotationResponse) => void;
  onWheel: (e: React.WheelEvent) => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
}

const FigmaCanvas: React.FC<FigmaCanvasProps> = ({
  frameId,
  imageUrl,
  imageWidth,
  imageHeight,
  annotations,
  selectedAnnotationId,
  highlightRect,
  imageLoading,
  getAnnotationPinPosition,
  canvasState,
  mode,
  onCanvasClick,
  onCanvasHover,
  onCanvasLeave,
  onAnnotationClick,
  onWheel,
  onMouseDown,
  onMouseMove,
  onMouseUp,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const getImageCoords = (e: React.MouseEvent) => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - canvasState.offsetX) / canvasState.scale;
    const y = (e.clientY - rect.top - canvasState.offsetY) / canvasState.scale;
    return { x, y };
  };

  const handleClick = (e: React.MouseEvent) => {
    if (mode !== 'annotate') return;
    const coords = getImageCoords(e);
    if (!coords) return;
    const { x, y } = coords;

    if (imageWidth > 0 && imageHeight > 0) {
      const xPercent = (x / imageWidth) * 100;
      const yPercent = (y / imageHeight) * 100;
      if (xPercent >= 0 && xPercent <= 100 && yPercent >= 0 && yPercent <= 100) {
        onCanvasClick(xPercent, yPercent);
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    onMouseMove(e);
    if (mode !== 'annotate' || !onCanvasHover) return;
    const coords = getImageCoords(e);
    if (!coords || imageWidth <= 0 || imageHeight <= 0) return;
    const { x, y } = coords;
    if (x >= 0 && x <= imageWidth && y >= 0 && y <= imageHeight) {
      onCanvasHover(x, y);
    } else if (onCanvasLeave) {
      onCanvasLeave();
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
        background: '#e5e5e5',
        cursor: mode === 'annotate' ? 'crosshair' : 'default',
      }}
      onWheel={onWheel}
      onMouseDown={onMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onCanvasLeave}
      onClick={handleClick}
    >
      {(imageLoading || !imageUrl) && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.7)',
            zIndex: 5,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#555', fontSize: '13px' }}>
            <div
              style={{
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                border: '2px solid #cbd5f5',
                borderTopColor: '#3b82f6',
                animation: 'figmaAnnotatorSpin 0.8s linear infinite',
              }}
            />
            Loading frame...
          </div>
          <style>
            {`
              @keyframes figmaAnnotatorSpin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}
      <div
        style={{
          transform: `translate(${canvasState.offsetX}px, ${canvasState.offsetY}px) scale(${canvasState.scale})`,
          transformOrigin: '0 0',
          position: 'relative',
          display: 'inline-block',
        }}
      >
        <img
          key={frameId || imageUrl}
          src={imageUrl}
          alt="Figma frame"
          style={{ display: 'block', maxWidth: 'none' }}
          draggable={false}
          onLoad={e => {
            // Image loaded — dimensions available from the img element
          }}
        />
        {highlightRect && (
          <div
            style={{
              position: 'absolute',
              left: `${highlightRect.left}%`,
              top: `${highlightRect.top}%`,
              width: `${highlightRect.width}%`,
              height: `${highlightRect.height}%`,
              border: '2px solid #3b82f6',
              background: 'rgba(59, 130, 246, 0.08)',
              borderRadius: '4px',
              pointerEvents: 'none',
              zIndex: 2,
              transition: 'all 0.15s',
            }}
          />
        )}
      </div>
      {/* Annotation pins overlay (screen-space) */}
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 3,
        }}
      >
        {annotations.map((annotation, idx) => {
          const pos = getAnnotationPinPosition
            ? getAnnotationPinPosition(annotation)
            : imageWidth && imageHeight
              ? {
                  x: (annotation.pinXPercent / 100) * imageWidth,
                  y: (annotation.pinYPercent / 100) * imageHeight,
                }
              : null;
          if (!pos) return null;
          const screenX = canvasState.offsetX + pos.x * canvasState.scale;
          const screenY = canvasState.offsetY + pos.y * canvasState.scale;
          return (
            <g key={annotation.id} style={{ pointerEvents: 'auto' }}>
              <AnnotationPin
                x={screenX}
                y={screenY}
                category={annotation.category}
                index={idx + 1}
                selected={selectedAnnotationId === annotation.id}
                onClick={() => onAnnotationClick(annotation)}
                scale={1.35}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default FigmaCanvas;
